import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { IconComponent } from '@design-system/buttons/icon/icon.component';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, startWith, distinctUntilChanged, shareReplay, skip } from 'rxjs/operators';
import { PermissionsService } from '@security/permissions/permissions.service';
import { NavDrawerRoute } from '../models/nav-actions.model';
import { BodyComponent } from '@design-system/typography/body/body.component';
import { TestIdDirective } from '@design-system/core/test-id.directive';
import { UserMenuComponent } from '../user-menu/user-menu.component';
import { getUrlPath } from '@utils/string-utils';
import {
  PageNavHeadingOverride,
  PageNavHeadingStateService,
} from '@design-system/layout/page-layout/page-nav-heading.state-service';
import { ThemePreferenceService } from '@design-system/theme/theme-preference.service';
import { UnityBrandLockupComponent } from '../../branding/unity-brand-lockup/unity-brand-lockup.component';
import { CssBreakpointsService } from '@design-system/layout/css-breakpoints.service';
import { NavTreeSectionComponent } from '../nav-tree-section/nav-tree-section.component';
import {
  collectAutoExpandedNavTitles,
  findActiveNavTitle,
  findNavBreadcrumb,
  isExpandableNavSection,
  NavBreadcrumb,
} from '../nav-route-tree.util';

@Component({
  selector: 'app-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IconComponent,
    BodyComponent,
    TestIdDirective,
    UserMenuComponent,
    UnityBrandLockupComponent,
    NavTreeSectionComponent,
  ],
})
export class NavBarComponent {
  mobileNavOpen = false;
  expandedSectionTitles = new Set<string>();

  mainNavDrawerRoutes$: Observable<NavDrawerRoute[]>;
  activeItem$: Observable<string>;
  displayBreadcrumb$: Observable<NavBreadcrumb>;

  private readonly currentUrl$: Observable<string>;
  private readonly navRouteContext$: Observable<[string, NavDrawerRoute[]]>;

  constructor(
    private router: Router,
    permissionsService: PermissionsService,
    private pageNavHeading: PageNavHeadingStateService,
    readonly theme: ThemePreferenceService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private cssBreakpoints: CssBreakpointsService,
  ) {
    this.mainNavDrawerRoutes$ = permissionsService.getMainNavDrawerRoutes();

    this.currentUrl$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => getUrlPath(this.router.url)),
      startWith(getUrlPath(this.router.url)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.navRouteContext$ = combineLatest([this.currentUrl$, this.mainNavDrawerRoutes$]).pipe(
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.activeItem$ = this.navRouteContext$.pipe(
      map(([url, routes]) => findActiveNavTitle(url, routes)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.displayBreadcrumb$ = combineLatest([
      this.navRouteContext$.pipe(map(([url, routes]) => findNavBreadcrumb(url, routes))),
      this.pageNavHeading.override$,
    ]).pipe(
      map(([fromNav, override]) => this.mergeBreadcrumb(fromNav, override)),
      distinctUntilChanged((a, b) => a.section === b.section && a.page === b.page),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.navRouteContext$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([url, routes]) => {
        const next = collectAutoExpandedNavTitles(url, routes);
        if (next.size !== this.expandedSectionTitles.size || [...next].some(t => !this.expandedSectionTitles.has(t))) {
          this.expandedSectionTitles = next;
          this.cdr.markForCheck();
        }
      });

    this.currentUrl$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closeMobileNav());

    this.cssBreakpoints.breakpoints$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(bp => {
        if (bp.isLargeScreen && this.mobileNavOpen) {
          this.mobileNavOpen = false;
          this.cdr.markForCheck();
        }
      });
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    this.closeMobileNav();
  }

  toggleMobileNav(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
    this.cdr.markForCheck();
  }

  closeMobileNav(): void {
    if (!this.mobileNavOpen) {
      return;
    }
    this.mobileNavOpen = false;
    this.cdr.markForCheck();
  }

  toggleSection(route: NavDrawerRoute): void {
    if (!isExpandableNavSection(route)) {
      return;
    }
    const next = new Set(this.expandedSectionTitles);
    if (next.has(route.title)) {
      next.delete(route.title);
    } else {
      next.add(route.title);
    }
    this.expandedSectionTitles = next;
    this.cdr.markForCheck();
  }

  private mergeBreadcrumb(
    fromNav: NavBreadcrumb,
    override: PageNavHeadingOverride | null
  ): NavBreadcrumb {
    if (!override?.page?.trim()) {
      return fromNav;
    }
    const page = override.page.trim();
    const section = override.section?.trim();
    if (section) {
      return { section, page };
    }
    return { section: page, page: null };
  }

  toggleDarkMode(): void {
    this.theme.toggle();
  }
}
